import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * První pomoc - V1 DRAFT - authored for structural completeness; NOT a
 * substitute for sign-off by a Czech osoba odborně způsobilá v prevenci rizik.
 * Legal refs: zákoník práce 262/2006 Sb. §102(6) a §103(1)(j) (zajištění první
 * pomoci). THEORY ONLY - the practical component (resuscitation practice)
 * cannot be delivered by e-learning; the content says so explicitly and a
 * certified hands-on course is recommended. Bilingual (cs-CZ + en-US).
 * Three chapters (postup u nehody | resuscitace | krvácení a úraz proudem) +
 * a graded quiz with one question per kind. Answer keys live here
 * (repo-privileged) and in the DB; stripped from the client payload by the API.
 */
export const firstAidProfile: TrainingProfile = {
  slug: 'first-aid',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar
  // bozp_hazard_profiles.title column (V1 Czech). Chapter titles are LocalizedText.
  title: 'První pomoc',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-postup-nehoda',
        title: t(
          'Postup u nehody: Rozhlédni se - Zavolej - Pomáhej',
          'At an accident: Look - Call - Help',
        ),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-nehoda'],
        blocks: [
          {
            id: 'h-nehoda',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Tři kroky, které si zapamatuješ', 'Three steps you will remember'),
          },
          {
            id: 'p-nehoda-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'U každé nehody platí stejná smyčka: **Rozhlédni se** (hrozí nebezpečí i tobě? proud, plyn, pád?), **Zavolej 155** (dispečer tě povede a zůstává na lince) a **Pomáhej** podle jeho pokynů. Zachránce, který se sám zraní, nikomu nepomůže.',
              'Every accident follows the same loop: **Look around** (are you in danger too? electricity, gas, falls?), **Call 155** (the dispatcher guides you and stays on the line) and **Help** by their instructions. A rescuer who gets injured helps no one.',
            ),
          },
          {
            id: 'c-nehoda-pravo',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Právní základ: **zákoník práce §102(6) a §103(1)(j)** - zajištění první pomoci na pracovišti. Neposkytnutí pomoci je trestný čin (§150 trestního zákoníku).',
              'Legal basis: **Labour Code §102(6) and §103(1)(j)** - first aid at the workplace. Failing to provide help is a criminal offence (§150 of the Criminal Code).',
            ),
          },
          {
            id: 'c-nehoda-prakticke',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'warning',
            markdown: t(
              'Tento kurz je **teoretický**. Praktický nácvik resuscitace nelze nahradit e-learningem - doporučujeme absolvovat certifikovaný praktický kurz (např. u ČČK).',
              'This course is **theory only**. Hands-on resuscitation practice cannot be replaced by e-learning - we recommend a certified practical course (e.g. with the Czech Red Cross).',
            ),
          },
          {
            id: 'ord-nehoda',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď kroky po příchodu k nehodě.',
              'Order the steps when you arrive at an accident.',
            ),
            items: [
              {
                id: 'on-1',
                label: t(
                  'Rozhlédni se - zajisti vlastní bezpečí',
                  'Look around - secure your own safety',
                ),
              },
              {
                id: 'on-2',
                label: t(
                  'Zjisti stav postiženého (vědomí, dýchání)',
                  'Check the casualty (consciousness, breathing)',
                ),
              },
              {
                id: 'on-3',
                label: t(
                  'Volej 155 a nech se vést dispečerem',
                  'Call 155 and follow the dispatcher',
                ),
              },
              {
                id: 'on-4',
                label: t(
                  'Poskytuj pomoc do příjezdu záchranky',
                  'Give help until the ambulance arrives',
                ),
              },
            ],
          },
          {
            id: 'k-nehoda',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              '**155** je záchranná služba (112 funguje také). Dispečer tě navede - nikdy nejsi na pomoc sám.',
              '**155** is the Czech ambulance line (112 works too). The dispatcher guides you - you are never helping alone.',
            ),
          },
        ],
      },
      {
        id: 'ch-resuscitace',
        title: t('Bezvědomí a resuscitace', 'Unconsciousness and resuscitation'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-dychani'],
        blocks: [
          {
            id: 'h-resuscitace',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Nedýchá? Stlačuj.', 'Not breathing? Compress.'),
          },
          {
            id: 'hs-bezvedomi',
            blockType: 'hotspotScene',
            schemaVersion: 1,
            scene: 'unconsciousCheck',
            hotspots: [
              {
                id: 'hs-osloveni',
                x: 0.7,
                y: 0.56,
                label: t(
                  'Hlasitě oslov a zatřes rameny - reaguje?',
                  'Speak loudly and shake the shoulders - any response?',
                ),
              },
              {
                id: 'hs-hlava',
                x: 0.8,
                y: 0.75,
                label: t(
                  'Zakloň hlavu a zvedni bradu - uvolníš dýchací cesty',
                  'Tilt the head back and lift the chin - opens the airway',
                ),
              },
              {
                id: 'hs-hrudnik',
                x: 0.56,
                y: 0.62,
                label: t(
                  'Sleduj hrudník max. 10 sekund - zvedá se?',
                  'Watch the chest for max 10 seconds - is it rising?',
                ),
              },
            ],
          },
          {
            id: 'p-resuscitace-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Nedýchá-li postižený normálně, zahaj **nepřímou srdeční masáž**: dlaně na střed hrudníku, stlačuj do hloubky **5-6 cm** frekvencí **100-120 stlačení za minutu** (rytmus písně „Rolničky"). Laik nemusí dýchat z úst do úst - kvalitní stlačování je priorita.',
              'If the casualty is not breathing normally, start **chest compressions**: hands on the centre of the chest, **5-6 cm deep** at **100-120 compressions per minute** (the rhythm of "Stayin\' Alive"). Bystanders may skip rescue breaths - quality compressions are the priority.',
            ),
          },
          {
            id: 'anim-rytmus',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'cprRhythm',
            caption: t(
              'Rytmus stlačování: 100-120 za minutu, do hloubky 5-6 cm. AED použij, jakmile je k dispozici - řiď se jeho pokyny a mezi nimi stlačuj dál.',
              'Compression rhythm: 100-120 per minute, 5-6 cm deep. Use the AED as soon as it is available - follow its prompts and keep compressing between them.',
            ),
          },
          {
            id: 'kc-dychani',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Postižený nereaguje a nedýchá normálně. Co uděláš nejdřív?',
              'The casualty is unresponsive and not breathing normally. What first?',
            ),
            choices: [
              {
                id: 'kc-a',
                label: t('Uložím ho do stabilizované polohy', 'Put them in the recovery position'),
              },
              {
                id: 'kc-b',
                label: t(
                  'Volám 155 a zahájím stlačování hrudníku',
                  'Call 155 and start chest compressions',
                ),
              },
              { id: 'kc-c', label: t('Hledám lékárničku', 'Look for a first-aid kit') },
            ],
            correctChoiceId: 'kc-b',
            explanation: t(
              'Stabilizovaná poloha patří jen tomu, kdo dýchá. Kdo nedýchá, potřebuje okamžitě resuscitaci a záchranku.',
              'The recovery position is only for someone who breathes. A non-breathing casualty needs immediate CPR and the ambulance.',
            ),
          },
          {
            id: 'k-resuscitace',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Stlačuj **100-120× za minutu, 5-6 cm hluboko**, AED použij, jakmile je k dispozici, a nepřestávej - stlačuj dál i mezi pokyny a výboji, dokud nepřijede záchranka.',
              'Compress **100-120× per minute, 5-6 cm deep**, use an AED as soon as it is available and do not stop - keep compressing between prompts and shocks until the ambulance arrives.',
            ),
          },
        ],
      },
      {
        id: 'ch-krvaceni-proud',
        title: t('Krvácení a úraz elektrickým proudem', 'Bleeding and electric shock'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-krvaceni'],
        blocks: [
          {
            id: 'h-krvaceni',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Zastav krvácení, odpoj proud', 'Stop the bleeding, cut the power'),
          },
          {
            id: 'ord-krvaceni',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď postup při masivním krvácení z končetiny.',
              'Order the steps for severe limb bleeding.',
            ),
            items: [
              {
                id: 'ok-1',
                label: t(
                  'Stlač ránu přímo (klidně přes oděv)',
                  'Press directly on the wound (through clothing is fine)',
                ),
              },
              {
                id: 'ok-2',
                label: t('Zvedni končetinu nad úroveň srdce', 'Raise the limb above heart level'),
              },
              { id: 'ok-3', label: t('Přilož tlakový obvaz', 'Apply a pressure bandage') },
              {
                id: 'ok-4',
                label: t('Volej 155, sleduj stav postiženého', 'Call 155, monitor the casualty'),
              },
            ],
          },
          {
            id: 'c-proud-danger',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'danger',
            markdown: t(
              'Úraz elektrickým proudem: **nikdy se postiženého nedotýkej, dokud neodpojíš zdroj** (vypni jistič, vytáhni přívod nehořlavou tyčí). Jinak se stáváš další obětí. Po zásahu proudem vždy k lékaři - riziko poruch srdečního rytmu trvá hodiny.',
              'Electric shock: **never touch the casualty until the source is disconnected** (trip the breaker, pull the lead away with a non-conductive pole). Otherwise you become the next victim. Always see a doctor after a shock - arrhythmia risk lasts for hours.',
            ),
          },
          {
            id: 'cl-lekarnicka',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Vím, kde mám v autě/brašně lékárničku',
                'I know where the first-aid kit is in my van/bag',
              ),
              t('Mám v telefonu uloženo 155 i 112', 'I have 155 and 112 saved in my phone'),
              t(
                'Popáleniny chladím vlažnou vodou 10-20 minut',
                'I cool burns with lukewarm water for 10-20 minutes',
              ),
              t(
                'Zaklíněný předmět z rány nikdy nevytahuji',
                'I never pull an impaled object out of a wound',
              ),
            ],
          },
          {
            id: 'k-krvaceni',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Přímý tlak zastaví většinu krvácení. U elektřiny platí: **nejdřív odpojit, pak zachraňovat** (viz školení Práce na elektrickém zařízení).',
              'Direct pressure stops most bleeding. With electricity: **disconnect first, rescue second** (see the Electrical Work course).',
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-proud-single',
      kind: 'single',
      chapterId: 'ch-krvaceni-proud',
      prompt: t(
        'Kolega dostal ránu proudem a leží u rozvaděče. Co uděláš jako první?',
        'A colleague was shocked and lies by the switchboard. What do you do first?',
      ),
      choices: [
        { id: 'a', label: t('Odtáhnu ho holýma rukama pryč', 'Drag them away with bare hands') },
        {
          id: 'b',
          label: t('Odpojím zdroj proudu (jistič, přívod)', 'Disconnect the power (breaker, lead)'),
        },
        {
          id: 'c',
          label: t('Začnu ihned stlačovat hrudník', 'Start chest compressions immediately'),
        },
      ],
      correct: 'b',
    },
    {
      id: 'q-poloha-tf',
      kind: 'truefalse',
      chapterId: 'ch-resuscitace',
      prompt: t(
        'Postiženého, který nedýchá, uložím do stabilizované polohy a počkám na záchranku.',
        'A casualty who is not breathing should be placed in the recovery position to await the ambulance.',
      ),
      correct: false,
    },
    {
      id: 'q-kroky-multi',
      kind: 'multi',
      chapterId: 'ch-postup-nehoda',
      prompt: t(
        'Co patří do prvních kroků u nehody? (vyber všechny správné)',
        'Which belong to the first steps at an accident? (select all that apply)',
      ),
      choices: [
        { id: 'a', label: t('Zajištění vlastního bezpečí', 'Securing my own safety') },
        { id: 'b', label: t('Volání na linku 155', 'Calling 155') },
        {
          id: 'c',
          label: t('Okamžitý převoz vlastním autem', 'Immediate transport in my own car'),
        },
        { id: 'd', label: t('Kontrola vědomí a dýchání', 'Checking consciousness and breathing') },
      ],
      correct: ['a', 'b', 'd'],
    },
    {
      id: 'q-frekvence-numeric',
      kind: 'numeric',
      chapterId: 'ch-resuscitace',
      prompt: t(
        'Jaká je minimální frekvence stlačování hrudníku za minutu?',
        'What is the minimum chest-compression rate per minute?',
      ),
      correct: 100,
      tolerance: 0,
    },
    {
      id: 'q-krvaceni-order',
      kind: 'ordering',
      chapterId: 'ch-krvaceni-proud',
      prompt: t('Seřaď postup při masivním krvácení.', 'Order the steps for severe bleeding.'),
      choices: [
        { id: 'a', label: t('Přímý tlak na ránu', 'Direct pressure on the wound') },
        { id: 'b', label: t('Zvednutí končetiny', 'Raise the limb') },
        { id: 'c', label: t('Tlakový obvaz', 'Pressure bandage') },
        { id: 'd', label: t('Volání 155 a sledování stavu', 'Call 155 and monitor') },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
